import {arktypeResolver} from '@hookform/resolvers/arktype'
import {type} from 'arktype'
import {klona} from 'klona'
import {FieldError, Form, Input, Label, Text, TextField} from 'react-aria-components'
import {Controller, useForm} from 'react-hook-form'

import {useAccountInformationValue} from '@/auth/hooks/useAccountInformation'
import noThrow from '@/utils/schema/noThrow'

import style from './ContactInfo2.module.scss'

// -----------------------------------------------------------------------------

export const Contact2 = type({
  name: type('string > 0')
    .narrow((data, ctx) => {
      if (data.trim().split(' ').length < 2) {
        ctx.reject({
          message: 'Full name should be at least 2 words',
        })
      }
      return true
    })
    .configure({
      message: 'Please enter a valid name',
    }),
  phone: type('10 <= string.numeric <= 11').configure({
    message: 'Please enter a valid phone number (10-11 digits)',
  }),
})

export type Contact2 = typeof Contact2.infer

const DEFAULT_CONTACT: Contact2 = {
  name: '',
  phone: '',
}

export function getDefaultContact() {
  return klona(DEFAULT_CONTACT)
}

// -----------------------------------------------------------------------------

const contactInfo2Storage = createJSONStorage<Contact2>(() => sessionStorage)
export const contact2Atom = atomWithStorage<Contact2>(
  'contact2',
  getDefaultContact(),
  contactInfo2Storage,
  {
    getOnInit: true,
  },
)

export function useContact() {
  return useAtom(contact2Atom)
}

export function useSetContact() {
  return useSetAtom(contact2Atom)
}

// -----------------------------------------------------------------------------

export interface ContactInfo2Control {
  submit: () => Promise<Contact2>
}

interface Props {
  ref?: Ref<ContactInfo2Control>
  className?: string
  initialContact?: Contact2
}

const NoThrowContact = noThrow(Contact2)

export default memo<Props>(function ContactInfo({ref, className, initialContact}) {
  const store = useStore()
  const account = useAccountInformationValue()
  const setContact = useSetContact()
  const defaultContact = store.get(contact2Atom)

  const formRef = useRef<HTMLFormElement>(null)

  // ---------------------------------------------------------------------------

  // TODO: later on let user pick from saved phone numbers
  const defaultValues = useMemo(
    () =>
      account
        ? {
            name: account.accountName,
            phone: '',
          }
        : (initialContact ?? defaultContact),
    [account, defaultContact, initialContact],
  )

  const {
    handleSubmit: handleContactSubmit,
    control: contactControl,
    trigger,
    getValues,
    formState,
    watch,
  } = useForm({
    mode: 'onTouched',
    resolver: arktypeResolver(NoThrowContact),
    defaultValues,
  })

  const latestFormState = useLatest(formState)
  const latestGetValues = useLatest(getValues)
  const latestTrigger = useLatest(trigger)

  useEffect(
    function persistDefaultValue() {
      setContact(defaultValues)
    },
    [setContact, defaultValues],
  )

  useEffect(
    function persistData() {
      const subscription = watch(() => {
        setContact(getValues())
      })

      return () => {
        subscription.unsubscribe()
      }
    },
    [watch, setContact, getValues],
  )

  useImperativeHandle(ref, () => {
    return {
      submit: async () => {
        const result = await latestTrigger.current(undefined, {shouldFocus: true})
        // eslint-disable-next-line @typescript-eslint/only-throw-error -- contains multiple errors
        if (!result) throw latestFormState.current.errors
        return latestGetValues.current()
      },
    }
  }, [])

  return (
    <Form
      ref={formRef}
      onSubmit={handleContactSubmit(() => {
        // do nothing
      })}
      className={clsx(style.Wrapper, className)}
    >
      <Controller
        control={contactControl}
        name='name'
        render={({field: {name, value, onChange, onBlur, ref}, fieldState: {invalid, error}}) => (
          <TextField
            name={name}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            validationBehavior='aria'
            isInvalid={invalid}
            isRequired
            className={style.Field}
            autoComplete='name'
          >
            <Label className={style.Label}>Full name</Label>
            <Input className={style.Input} ref={ref} />
            {!!error?.message && <FieldError className={style.Error}>{error.message}</FieldError>}
          </TextField>
        )}
      />
      <Controller
        control={contactControl}
        name='phone'
        render={({field: {name, value, onChange, onBlur, ref}, fieldState: {invalid, error}}) => (
          <TextField
            name={name}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            validationBehavior='aria'
            isInvalid={invalid}
            isRequired
            className={style.Field}
            autoComplete='tel'
          >
            <Label className={style.Label}>Phone number</Label>
            <Input className={style.Input} ref={ref} />
            {!error?.message && (
              <Text slot='description' className={style.Description}>
                Numbers only (10-11 digits)
              </Text>
            )}
            {!!error?.message && <FieldError className={style.Error}>{error.message}</FieldError>}
          </TextField>
        )}
      />
    </Form>
  )
})
